/**
 * Scaffolder field extension for the spoke admins on the "Owner details" step.
 *
 * Renders one AddUserList. The value keeps the selected users and a
 * comma-separated list of their names, which the review step shows.
 *
 * The admins are recorded on the request (ADO work item) and written to the
 * spoke's persona tfvars, which put them into GP_GDL_<SPOKE>_SPOKE_ADMINS.
 * They must be normal accounts: they sign in to Backstage.
 */
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { AccountKind, accountKindRefusal } from '@internal/datasphere-personas';
import { AddUserList, UserEntry } from '../AddUserList';

export interface SpokeAdminsFieldValue {
  admins: UserEntry[];
  /** Comma-separated display names, for the review step. */
  admins_names: string;
}

const EMPTY_VALUE: SpokeAdminsFieldValue = {
  admins: [],
  admins_names: '',
};

export const SPOKE_ADMINS_REQUIRED_MESSAGE = 'Add at least one spoke admin.';

/** Error messages for a field value; empty when the value is valid. */
export const spokeAdminsErrors = (
  value: SpokeAdminsFieldValue | undefined,
): string[] => {
  if (!value?.admins?.length) {
    return [SPOKE_ADMINS_REQUIRED_MESSAGE];
  }

  return value.admins.flatMap(
    user => accountKindRefusal(user, AccountKind.Normal, 'Spoke Admin') ?? [],
  );
};

export const SpokeAdminsField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<SpokeAdminsFieldValue>) => {
  const value: SpokeAdminsFieldValue = formData ?? EMPTY_VALUE;

  const handleChange = (admins: UserEntry[]) =>
    onChange({
      admins,
      admins_names: admins.map(u => u.displayName).join(', '),
    });

  return (
    <AddUserList
      users={value.admins}
      onChange={handleChange}
      label="Search for a spoke admin"
      helperText="Add the people who will do the hands-on work for this spoke. Use their normal account, not their admin account."
      required={required}
      accountKind={AccountKind.Normal}
      rawErrors={rawErrors?.length ? rawErrors : undefined}
    />
  );
};
